import React from 'react';
import { distanceToLine } from './utils';
import jsts from 'jsts';

// Distance from point p to segment ab. Pure 2D, no jsts.
function pointToSegmentDistance(p, a, b) {
  const vx = b[0] - a[0];
  const vy = b[1] - a[1];
  const lenSq = vx * vx + vy * vy;
  if (lenSq === 0) return Math.hypot(p[0] - a[0], p[1] - a[1]);
  let t = ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const qx = a[0] + t * vx;
  const qy = a[1] + t * vy;
  return Math.hypot(p[0] - qx, p[1] - qy);
}

// Ray (origin + t*dir, t >= 0) vs segment (a→b) intersection.
// Returns { t, point } on hit or null. `t` is the ray parameter (distance if
// dir is unit) — useful for "nearest" comparisons.
function raySegmentIntersection(origin, dir, a, b, eps) {
  const rx = dir[0], ry = dir[1];
  const sx = b[0] - a[0], sy = b[1] - a[1];
  const denom = rx * sy - ry * sx;
  if (Math.abs(denom) < eps) return null; // parallel
  const qpx = a[0] - origin[0];
  const qpy = a[1] - origin[1];
  const t = (qpx * sy - qpy * sx) / denom;   // along the ray
  const u = (qpx * ry - qpy * rx) / denom;   // along the segment (0..1)
  if (t < 0) return null;
  if (u < -eps || u > 1 + eps) return null;
  return { t, point: [origin[0] + t * rx, origin[1] + t * ry] };
}


class Line {
  constructor(points = [], type = "", id = "", intersected = false, gsd = 1, group = 0) {
    this.points = points.filter(point => 
      Array.isArray(point) && point.length === 2 && 
      !isNaN(point[0]) && !isNaN(point[1])
    );
    this.id = id || Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    this.type = type; // this is boundary, endrow, insiderow
    this.waypoints = [];
    this.intersected = intersected;
    this.intersectedStart = false;
    this.intersectedEnd = false;
    this.gsd = gsd;
    this.group = group;
  }
    addPoint(point) {
        const [x, y] = point;
        const lastPoint = this.points[this.points.length - 1];
        if (!lastPoint || lastPoint[0] !== x || lastPoint[1] !== y) {
                this.points.push(point);
        }
    }

    removePoint(index) {
        this.points.splice(index, 1);
    }

    move(dx, dy) {
        this.points = this.points.map(([x, y]) => [x + dx, y + dy]);
    }
    
    isPointNear(point, threshold) {
        if (this.points.length < 2) return false;
        const distance = distanceToLine(this.points, point);
        return distance <= threshold * 10; // Increased threshold for easier selection
    }

    // Shortest perpendicular distance from `point` to this polyline.
    // Returns Infinity for degenerate lines so callers can safely compare.
    distanceTo(point) {
        if (!this.points || this.points.length < 2) return Infinity;
        return distanceToLine(this.points, point);
    }

    updatePoint(index, dx, dy) {
        if (index >= 0 && index < this.points.length) {
            this.points[index] = [
                this.points[index][0] + dx,
                this.points[index][1] + dy
            ];
        }
    }

    generateWaypoints(gsd) {
      if (this.points.length < 2) return;

      const intervalPixel = (25/gsd) // 25 cm
      const waypoints = [];
      for (let i = 0; i < this.points.length - 1; i++) {
        const [x1, y1] = this.points[i];
        const [x2, y2] = this.points[i + 1];
        const dx = x2 - x1;
        const dy = y2 - y1;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const numPoints = Math.ceil(distance / intervalPixel);

        for (let j = 0; j < numPoints; j++) {
          const t = j / numPoints;
          const x = x1 + t * dx;
          const y = y1 + t * dy;
          waypoints.push([x, y]);
        }
      }
      this.waypoints = waypoints;
    }
    getData() {
      return {
        id: this.id,
        points: this.points,
        waypoints: this.waypoints,
        type: this.type
      };
    }


    clipToInnerBoundary(innerBoundaryPolygon) {
      if (this.points.length < 2) {
        // console.log("Line ID:", this.id, "has less than 2 points");
        return;
      }
    
      try {
        const geometryFactory = new jsts.geom.GeometryFactory();
        
        // Create line string
        const lineCoords = this.points.map(p => new jsts.geom.Coordinate(p[0], p[1]));
        const lineString = geometryFactory.createLineString(lineCoords);
    
        // Perform intersection
        const intersection = lineString.intersection(innerBoundaryPolygon);
        if (intersection.isEmpty()) {
          // console.log("Line ID:", this.id, "does not intersect with inner boundary");
          return;
        }
    
        // Update points
        this.points = intersection.getCoordinates().map(c => [c.x, c.y]);
      } catch (error) {
        // console.error("Error in clipToInnerBoundary:", error);
      }
    }

    clipToOtherLines(lines) {
      if (this.points.length < 2) {
        // console.log(`Line ID: ${this.id} has less than 2 points. Skipping intersection.`);
        return;
      }
    
      const geometryFactory = new jsts.geom.GeometryFactory();
      let thisLineString;
      try {
        thisLineString = geometryFactory.createLineString(
          this.points.map(p => new jsts.geom.Coordinate(p[0], p[1]))
        );
      } catch (error) {
        // console.error(`Error creating LineString for Line ID: ${this.id}`, error);
        // console.log("Points:", JSON.stringify(this.points));
        return;
      }
    
      lines.forEach(otherLine => {
        if (otherLine.id !== this.id && otherLine.points.length >= 2) {
          let otherLineString;
          try {
            otherLineString = geometryFactory.createLineString(
              otherLine.points.map(p => new jsts.geom.Coordinate(p[0], p[1]))
            );
          } catch (error) {
            // console.error(`Error creating LineString for other Line ID: ${otherLine.id}`, error);
            // console.log("Other Line Points:", JSON.stringify(otherLine.points));
            return;
          }
    
          try {
            const intersection = thisLineString.intersection(otherLineString);
            if (!intersection.isEmpty()) {
              // Collect every crossing coordinate. We deliberately do NOT
              // switch on `getGeometryType()` here: when this line crosses a
              // multi-segment polyline (curved headland) in two or more
              // places, JSTS returns a MultiPoint whose geometry-type string
              // is the generic "Geometry" rather than "MultiPoint". The
              // previous switch dropped that case and only `coords[0]` ever
              // got applied — the visible bug where a straight inside-row
              // would plow through a curved headland on one side.
              // `getCoordinates()` works uniformly across Point / MultiPoint /
              // GeometryCollection / Geometry, so just use it.
              const coords = intersection.getCoordinates();

              if (coords.length > 0) {
                // Trim semantics: a line crossing another line in N places
                // should be clipped at the *outermost* crossings — the one
                // closest to the start (smallest parameter along the line)
                // and the one closest to the end (largest parameter). Anything
                // beyond those is the "outside" stub that needs to be cut.
                //
                // Previously only coords[0] was applied, so a straight line
                // crossing a curved/zigzag line in two places had only one
                // end clipped and the other plowed through. A nearest-endpoint
                // rule isn't enough either, because both crossings can sit
                // closer to one endpoint than the other (common when one
                // endpoint is near a corner of the curved line) — so we
                // project crossings onto the line's direction.
                const last = this.points.length - 1;
                const start = this.points[0];
                const end = this.points[last];
                const dx = end[0] - start[0];
                const dy = end[1] - start[1];
                const lenSq = dx * dx + dy * dy;
                if (lenSq > 0 && last > 0) {
                  // Project each crossing onto the line, parameter t in [0,1]
                  // for points between start and end.
                  let minT = null, maxT = null;
                  for (const c of coords) {
                    const t = ((c.x - start[0]) * dx + (c.y - start[1]) * dy) / lenSq;
                    const tagged = { t, point: [c.x, c.y] };
                    if (!minT || t < minT.t) minT = tagged;
                    if (!maxT || t > maxT.t) maxT = tagged;
                  }
                  if (minT === maxT) {
                    // Single crossing: snap whichever endpoint is closer to
                    // it (the original behavior for the single-crossing case).
                    const dStart = (start[0]-minT.point[0])**2 + (start[1]-minT.point[1])**2;
                    const dEnd   = (end[0]  -minT.point[0])**2 + (end[1]  -minT.point[1])**2;
                    if (dStart <= dEnd) {
                      this.points[0] = minT.point;
                      this.intersectedStart = true;
                    } else {
                      this.points[last] = minT.point;
                      this.intersectedEnd = true;
                    }
                  } else {
                    // Multiple crossings: trim each end at its outermost crossing.
                    this.points[0] = minT.point;
                    this.intersectedStart = true;
                    this.points[last] = maxT.point;
                    this.intersectedEnd = true;
                  }
                }
                thisLineString = geometryFactory.createLineString(
                  this.points.map(p => new jsts.geom.Coordinate(p[0], p[1]))
                );
              }
            }
          } catch (error) {
            // console.error(`Error during intersection calculation between Line ID: ${this.id} and Line ID: ${otherLine.id}`, error);
            // console.log("This Line Points:", JSON.stringify(this.points));
            // console.log("Other Line Points:", JSON.stringify(otherLine.points));
          }
        }
      });
    }

    findNearestPoint(coord, points) {
      let minDist = Infinity;
      let nearestIndex = 0;
      points.forEach((point, index) => {
        const dist = Math.sqrt(Math.pow(point[0] - coord.x, 2) + Math.pow(point[1] - coord.y, 2));
        if (dist < minDist) {
          minDist = dist;
          nearestIndex = index;
        }
      });
      return { index: nearestIndex, distance: minDist };
    }

    extendToOtherLines(lines) {
      if (this.points.length < 2 || (this.intersectedStart && this.intersectedEnd)) {
        return;
      }

      const geometryFactory = new jsts.geom.GeometryFactory();
      const firstSegmentUnitVector = this.getUnitVector(this.points[1], this.points[0]);
      const lastSegmentUnitVector = this.getUnitVector(this.points[this.points.length - 2], this.points[this.points.length - 1]);
      // console.log("firstSegmentUnitVector 193", firstSegmentUnitVector);
      // console.log("lastSegmentUnitVector 193", lastSegmentUnitVector);
      let nearestStart = null;
      let nearestEnd = null;
      
      if (!this.intersectedStart) {
        const extendedFirst = this.createExtendedSegment(this.points[0], firstSegmentUnitVector);
        // console.log("extendedFirst 200", extendedFirst);
        nearestStart = this.findNearestIntersection(this.points[0],extendedFirst, lines, geometryFactory);
        // console.log("nearestStart 203", nearestStart);
      }
      
      if (!this.intersectedEnd) {
        const extendedLast = this.createExtendedSegment(this.points[this.points.length - 1], lastSegmentUnitVector);
        // console.log("extendedLast 207", extendedLast);
        nearestEnd = this.findNearestIntersection(this.points[this.points.length - 1],extendedLast, lines, geometryFactory);
        // console.log("nearestEnd 209", nearestEnd);
      }
      
      this.updateLinePoints(
        nearestStart || this.points[0],
        nearestEnd || this.points[this.points.length - 1]
      );
      
      if (nearestStart) {
        this.intersectedStart = true;
      }
      if (nearestEnd) {
        this.intersectedEnd = true;
      }
      
      console.log("this.id 223", this.id);
      console.log("this.intersectedStart 224", this.intersectedStart);
      console.log("this.intersectedEnd 224", this.intersectedEnd);
    
    }


    findNearestIntersection(startPoint, extendedSegment, lines, geometryFactory) {
      let nearestPoint = null;
      let minDistance = Infinity;
    
      const getIntersectedPoint = (intersection) => {
        if (!intersection.isEmpty()) {
          const intersectionPoint = intersection.getCoordinate();
          return [intersectionPoint.x, intersectionPoint.y];
      };
      }
      lines.forEach(line => {
        if (line.id !== this.id) {
          const otherLineString = geometryFactory.createLineString(line.points.map(p => new jsts.geom.Coordinate(p[0], p[1])));
          const intersection = extendedSegment.intersection(otherLineString);
          
          if (!intersection.isEmpty()) {
            console.log("intersection is not empty 239");
            console.log("intersection 240", intersection);
            // const intersectionPoint = getIntersectedPoint(intersection);
            // console.log("intersectionPoint 243", intersectionPoint);
            // console.log("extendedSegment start point 243", startPoint);

            intersection.getCoordinates().forEach(coord => {
              const distance = this.distance(
                [coord.x, coord.y],
                [startPoint[0], startPoint[1]]
              );
              console.log("distance 250", distance);
              if (distance < minDistance) {
                minDistance = distance;
                nearestPoint = [coord.x, coord.y];
              }
            });
          } else {
            // console.log("intersection is empty 245");
          }
        }
      });
    
      console.log("nearestPoint", nearestPoint);
      return nearestPoint;
    }
  

    // Helper method to calculate distance between two points
    distance(p1, p2) {
      return Math.sqrt((p2[0] - p1[0])**2 + (p2[1] - p1[1])**2);
    }

    getUnitVector(p1, p2) {
      const dx = p2[0] - p1[0];
      const dy = p2[1] - p1[1];
      const length = Math.sqrt(dx * dx + dy * dy);
      if (length === 0) {
        return null; // Points are the same
      }
      return [dx / length, dy / length];
    }

    extendToInnerBoundary(innerBoundaryPoints) {
      // console.log("innerBoundaryPoints in line.js:", innerBoundaryPoints);
      console.log("this.id 292", this.id);
      console.log("this.intersectedStart 293", this.intersectedStart);
      console.log("this.intersectedEnd 294", this.intersectedEnd);
      if (this.points.length < 2 || (this.intersectedStart && this.intersectedEnd)) {
        // console.log("line ID: ", this.id, "has less than 2 points or is intersected");
        return;
      }

      const geometryFactory = new jsts.geom.GeometryFactory();
      // Create inner boundary polygon
      const boundaryCoords = innerBoundaryPoints.map(point => new jsts.geom.Coordinate(point[0], point[1]));
      if (boundaryCoords[0].x !== boundaryCoords[boundaryCoords.length - 1].x || 
          boundaryCoords[0].y !== boundaryCoords[boundaryCoords.length - 1].y) {
        boundaryCoords.push(boundaryCoords[0]);
      }
      const shell = geometryFactory.createLinearRing(boundaryCoords);
      const innerBoundaryPolygon = geometryFactory.createPolygon(shell);
      // function to get the points from the intersection
      const getIntersectedPoint = (intersection, point) => {
        let closestPoint = null;
        let minDistance = 0;
        if (!intersection.isEmpty()) {
          intersection.getCoordinates().forEach(coord => {
            const distance = this.distance([coord.x, coord.y], point);
            if (distance > minDistance) {
              minDistance = distance;
              closestPoint = [coord.x, coord.y];
            }
          });
          return closestPoint;
        }
        return point;
      }

      let firstIntersectionPoint;
      let lastIntersectionPoint;

      console.log("this.intersectedStart 315", this.intersectedStart);
      console.log("this.intersectedEnd 316", this.intersectedEnd);


      if (!this.intersectedStart) {
        // // check if the first point is already intersected with the inner boundary
        // const geometryFactory = new jsts.geom.GeometryFactory();
        // const coords = this.points.map(p => new jsts.geom.Coordinate(p[0], p[1]));
        // const linestring = geometryFactory.createLineString(coords);
        // const firstIntersection = linestring.intersection(innerBoundaryPolygon);
        // console.log("firstIntersection 310", firstIntersection);
        // console.log("firstIntersection.isEmpty() 311", firstIntersection.isEmpty());
        // // const PointInPolygon = 
        // if (!firstIntersection.isEmpty()) {
        //   firstIntersectionPoint = getIntersectedPoint(firstIntersection, this.points[0]);
        //   // this.intersectedStart = true;
        // } else {
          const bufferedPolygon = innerBoundaryPolygon.buffer(1);
          const firstSegmentUnitVector = this.getUnitVector(this.points[1], this.points[0]);
          const firstSegmentExtended = this.createExtendedSegment(this.points[0], firstSegmentUnitVector);
          const firstIntersectionExtended = firstSegmentExtended.intersection(bufferedPolygon);
          console.log("firstSegmentExtended:", firstSegmentExtended.toString());
          console.log("innerBoundaryPolygon:", innerBoundaryPolygon.toString());
          console.log("Is firstSegmentExtended valid:", firstSegmentExtended.isValid());
          console.log("Is innerBoundaryPolygon valid:", innerBoundaryPolygon.isValid());
          console.log("firstIntersectionExtended 320 is empty", firstIntersectionExtended.isEmpty());
          if (!firstIntersectionExtended.isEmpty()) {
          firstIntersectionPoint = getIntersectedPoint(firstIntersectionExtended, this.points[0]);
          this.intersectedStart = true;
          }else {
            console.log("firstIntersectionExtended is empty 328");
            firstIntersectionPoint = this.points[0];
          }
       }
        else {
          console.log(" inside else this.intersectedStart")
        firstIntersectionPoint = this.points[0];
      }
    
      if (!this.intersectedEnd) { 
        // check if the last point is already intersected with the inner boundary
        // const coords = this.points.map(p => new jsts.geom.Coordinate(p[0], p[1]));
        // const linestring = geometryFactory.createLineString(coords);
        // const lastIntersection = linestring.intersection(innerBoundaryPolygon);
        // console.log("lastIntersection 334", lastIntersection);
        // if (!lastIntersection.isEmpty()) {
        //   lastIntersectionPoint = getIntersectedPoint(lastIntersection, this.points[this.points.length - 1]);
        //   // this.intersectedEnd = true;
        // } else {
        console.log(" inside if !this.intersectedEnd")
        const bufferedPolygon = innerBoundaryPolygon.buffer(1);
        const lastSegmentUnitVector = this.getUnitVector(this.points[this.points.length - 2], this.points[this.points.length - 1]);
        // console.log("lastSegmentUnitVector 330", lastSegmentUnitVector);
        const lastSegmentExtended = this.createExtendedSegment(this.points[this.points.length - 1], lastSegmentUnitVector);
        // console.log("lastSegmentExtended 332", lastSegmentExtended);
        const lastIntersectionExtended = lastSegmentExtended.intersection(bufferedPolygon);
        console.log("lastIntersectionExtended 334", lastIntersectionExtended);
        console.log("lastIntersectionExtended.isEmpty() 335", lastIntersectionExtended.isEmpty());
        console.log("lastSegmentExtended:", lastSegmentExtended.toString());
        console.log("innerBoundaryPolygon:", innerBoundaryPolygon.toString());
        console.log("Is lastSegmentExtended valid:", lastSegmentExtended.isValid());
        console.log("Is innerBoundaryPolygon valid:", innerBoundaryPolygon.isValid());
        console.log("lastIntersectionExtended 340 is empty", lastIntersectionExtended.isEmpty());
        
        
        if (!lastIntersectionExtended.isEmpty()) {
          lastIntersectionPoint = getIntersectedPoint(lastIntersectionExtended, this.points[this.points.length - 1]);
          this.intersectedEnd = true;
        } else {
          console.log("lastIntersectionExtended is empty 341");
          lastIntersectionPoint = this.points[this.points.length - 1];
        }
      }
      // }
      else {
        console.log(" inside else this.intersectedEnd")
        lastIntersectionPoint = this.points[this.points.length - 1];
      }
      
      // console.log("firstIntersectionPoint", firstIntersectionPoint);
      // console.log("lastIntersectionPoint", lastIntersectionPoint);
      console.log("this.id 369", this.id);
      console.log("this.intersectedStart 370", this.intersectedStart);
      console.log("this.intersectedEnd 371", this.intersectedEnd);
      this.updateLinePoints(firstIntersectionPoint, lastIntersectionPoint);
    }
  
    createExtendedSegment(start, unitVector) {
      const geometryFactory = new jsts.geom.GeometryFactory();
      const factor = 10000; // Adjust as needed
        
      const dx = unitVector[0] * factor;
      const dy = unitVector[1] * factor;
      // const length = Math.sqrt(dx * dx + dy * dy);

      const extendedStart = new jsts.geom.Coordinate(
        start[0],
        start[1]
      );
      const extendedEnd = new jsts.geom.Coordinate(
        start[0] + dx ,
        start[1] + dy 
      );

      return geometryFactory.createLineString([extendedStart, extendedEnd]);
    }
    

    isPointCloseToPolygon(point, polygonPoints, tolerance = 0.01) {
      for (let i = 0; i < polygonPoints.length; i++) {
        const p1 = polygonPoints[i];
        const p2 = polygonPoints[(i + 1) % polygonPoints.length];
        const distance = this.distanceToLineSegment(point, p1, p2);
        if (distance <= tolerance) {
          return true;
        }
      }
      return false;
    }

    updateLinePoints(firsPoint, lastPoint) {    
      this.points = [firsPoint, ...this.points.slice(1, -1), lastPoint];
    }

    updateLineWithIntersectionPoint(intersectionPoint, lineString) {
      // const start = lineString.getCoordinateN(0);
      // const end = lineString.getCoordinateN(lineString.getNumPoints() - 1);
      
      if (this.isCloserToStart(intersectionPoint, lineString)) {
        this.points = [[intersectionPoint.x, intersectionPoint.y], ...this.points.slice(1)];
      } else {
        this.points = [...this.points.slice(0, -1), [intersectionPoint.x, intersectionPoint.y]];
      }
    }
    
    updateLineWithMultipleIntersectionPoints(intersectionPoints, lineString) {
      // const start = lineString.getCoordinateN(0);
      // const end = lineString.getCoordinateN(lineString.getNumPoints() - 1);
      
      const startPoint = intersectionPoints.reduce((closest, point) => 
        this.isCloserToStart(point, lineString) ? point : closest
      );
      const endPoint = intersectionPoints.reduce((farthest, point) => 
        !this.isCloserToStart(point, lineString) ? point : farthest
      );
    
      this.points = [
        [startPoint.x, startPoint.y],
        ...this.points.slice(1, -1),
        [endPoint.x, endPoint.y]
      ];
    }
    
    isCloserToStart(point, lineString) {
      const start = lineString.getCoordinateN(0);
      const end = lineString.getCoordinateN(lineString.getNumPoints() - 1);
      return point.distance(start) < point.distance(end);
    }


    extendLine(lineString) {
      const start = lineString.getCoordinateN(0);
      const end = lineString.getCoordinateN(lineString.getNumPoints() - 1);
      const factor = 1000; // Adjust as needed

      const dx = end.x - start.x;
      const dy = end.y - start.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      const extendedStart = new jsts.geom.Coordinate(
        start.x - (dx / length) * factor,
        start.y - (dy / length) * factor
      );
      const extendedEnd = new jsts.geom.Coordinate(
        end.x + (dx / length) * factor,
        end.y + (dy / length) * factor
      );

      return new jsts.geom.GeometryFactory().createLineString(
        [extendedStart, extendedEnd]
      );
    }


  
    distanceToLineSegment(point, lineStart, lineEnd) {
      const x = point[0];
      const y = point[1];
      const x1 = lineStart.x;
      const y1 = lineStart.y;
      const x2 = lineEnd.x;
      const y2 = lineEnd.y;
    
      const A = x - x1;
      const B = y - y1;
      const C = x2 - x1;
      const D = y2 - y1;
    
      const dot = A * C + B * D;
      const lenSq = C * C + D * D;
      let param = -1;
      if (lenSq !== 0) {
        param = dot / lenSq;
      }
    
      let xx, yy;
    
      if (param < 0) {
        xx = x1;
        yy = y1;
      } else if (param > 1) {
        xx = x2;
        yy = y2;
      } else {
        xx = x1 + param * C;
        yy = y1 + param * D;
      }
    
      const dx = x - xx;
      const dy = y - yy;
      return Math.sqrt(dx * dx + dy * dy);
    }


    validateGeometry() {
      // Remove any undefined or null points
      this.points = this.points.filter(point => point && point.length === 2 && !isNaN(point[0]) && !isNaN(point[1]));
    
      // Remove duplicate points
      const uniquePoints = this.points.filter((point, index, self) =>
        index === self.findIndex((t) => (t[0] === point[0] && t[1] === point[1]))
      );
      
      if (uniquePoints.length !== this.points.length) {
        console.warn("Line ID:", this.id, "contained duplicate points. Removed duplicates.");
        this.points = uniquePoints;
      }
      
      if (this.points.length < 2) {
        console.error("Line ID:", this.id, "has less than 2 points after validation.");
        return false;
      }
      
      // Check for very small segments
      for (let i = 1; i < this.points.length; i++) {
        const dx = this.points[i][0] - this.points[i-1][0];
        const dy = this.points[i][1] - this.points[i-1][1];
        if (Math.sqrt(dx*dx + dy*dy) < 1e-10) {
          console.warn("Line ID:", this.id, "contains very small segments. This may cause issues.");
        }
      }
    
      return true;
    }


  setType(type) {
    this.type = type;
  }

  setGroup(group) {
    this.group = group;
  } 

  renderSVG(scale, isSelected, isHovered, isMultipleSelected, alpha, isFlashing) {
    if (this.points.length < 2) return null;
    const strokeWidth = Math.max(5, 50 / scale);

    const baseColor = () => {
      switch (this.type) {
        case 'turn_row': return 'purple';
        case 'inside_row': return 'green';
        case 'end_row': return 'blue';
        default: return 'yellow';
      }
    };
    const getColor = () => {
      if (isFlashing) return 'orange';
      if (isSelected || isMultipleSelected) return 'cyan';
      if (isHovered) return 'purple';
      return baseColor();
    };

    // Dashed overlay on the extended portion at each end. Uses the line's
    // native color so it reads as "a continuation of this line". The overlay
    // sits on top of the solid stroke.
    const lineColor = baseColor();
    const dashLen = Math.max(8, 60 / scale);
    const gapLen = Math.max(4, 30 / scale);
    const overlay = (a, b) => (
      <polyline
        points={`${a[0]},${a[1]} ${b[0]},${b[1]}`}
        fill="none"
        stroke={lineColor}
        strokeWidth={strokeWidth * 0.9}
        strokeDasharray={`${dashLen},${gapLen}`}
        strokeOpacity={isSelected || isHovered ? 1 : (alpha ?? 1)}
      />
    );

    return (
      <>
        <polyline
          points={this.points.map(([x, y]) => `${x},${y}`).join(' ')}
          fill="none"
          stroke={getColor()}
          strokeWidth={(isSelected || isHovered || isFlashing) ? strokeWidth * 2 : strokeWidth}
          strokeOpacity={isSelected || isHovered || isFlashing ? 1 : alpha}
        />
        {this.extendedStart && this.points.length >= 1 && overlay(this.extendedStart, this.points[0])}
        {this.extendedEnd && this.points.length >= 1 && overlay(this.points[this.points.length - 1], this.extendedEnd)}
      </>
    );
  }

toString() {
    return `Line(id: ${this.id}, points: ${this.points.length}, waypoints: ${this.waypoints.length})`;
  }

  distanceToPoint(point) {
    return Math.min(...this.points.map(linePoint =>
      Math.sqrt(Math.pow(linePoint[0] - point[0], 2) + Math.pow(linePoint[1] - point[1], 2))
    ));
  }

  // === New Extend Line implementation ===
  //
  // Extends this line's open endpoints outward until the ray hits the nearest
  // target: another line's segments, the inner/outer boundary polygon edges,
  // or (as a last-resort backstop) the image-edge rectangle.
  //
  // Endpoints with `intersectedStart`/`intersectedEnd` set are skipped, so
  // running twice is a no-op. Dragging an endpoint should clear the
  // corresponding flag so Extend will reconsider it next time.
  //
  // Parameters:
  //   otherLines:             Line[]  — any line on canvas EXCEPT this one
  //   innerBoundaryPoints:    [x,y][] — closed ring (may be empty)
  //   outerBoundaryPoints:    [x,y][] — closed ring (may be empty)
  //   imageBounds:            { width, height }
  //   tolerance:              number  — image-px tolerance for "already on target"
  //
  // Returns an object describing what happened:
  //   { startExtended: bool, endExtended: bool, startSkipped: 'alreadyIntersected'|'degenerate'|'onTarget'|null, endSkipped: ...|null }
  extendOpenEndpoints(otherLines, innerBoundaryPoints, outerBoundaryPoints, imageBounds, tolerance) {
    const result = { startExtended: false, endExtended: false, startSkipped: null, endSkipped: null };
    if (this.points.length < 2) {
      result.startSkipped = 'degenerate';
      result.endSkipped = 'degenerate';
      return result;
    }

    // Build a list of segments from all target sources. Each segment = [A, B].
    const targetSegments = [];
    for (const line of otherLines) {
      if (!line || line.id === this.id) continue;
      for (let i = 0; i < line.points.length - 1; i++) {
        targetSegments.push([line.points[i], line.points[i + 1]]);
      }
    }
    const addRingSegments = (ring) => {
      if (!ring || ring.length < 2) return;
      for (let i = 0; i < ring.length - 1; i++) {
        targetSegments.push([ring[i], ring[i + 1]]);
      }
      // Close the ring if not already closed.
      const first = ring[0], last = ring[ring.length - 1];
      if (first[0] !== last[0] || first[1] !== last[1]) {
        targetSegments.push([last, first]);
      }
    };
    addRingSegments(innerBoundaryPoints);
    addRingSegments(outerBoundaryPoints);

    // Image-edge rectangle as ultimate backstop. Rays never escape.
    if (imageBounds && imageBounds.width && imageBounds.height) {
      const W = imageBounds.width, H = imageBounds.height;
      targetSegments.push([[0, 0], [W, 0]]);
      targetSegments.push([[W, 0], [W, H]]);
      targetSegments.push([[W, H], [0, H]]);
      targetSegments.push([[0, H], [0, 0]]);
    }

    const EPS = 1e-6;
    const tol = tolerance || 2;

    // Helper: does the endpoint already sit on any target (within tolerance)?
    const endpointOnTarget = (p) => {
      for (const [a, b] of targetSegments) {
        if (pointToSegmentDistance(p, a, b) <= tol) return true;
      }
      return false;
    };

    // Extend one endpoint. `endIsFirst` = true → extend backward from points[0].
    const extendOne = (endIsFirst) => {
      const anchor = endIsFirst ? this.points[0] : this.points[this.points.length - 1];
      const inner = endIsFirst ? this.points[1] : this.points[this.points.length - 2];
      // Direction vector points outward from the line.
      const dx = anchor[0] - inner[0];
      const dy = anchor[1] - inner[1];
      const len = Math.hypot(dx, dy);
      if (len < EPS) return { extended: false, skipped: 'degenerate' };

      // Already-on-target shortcut.
      if (endpointOnTarget(anchor)) {
        return { extended: false, skipped: 'onTarget' };
      }

      const ux = dx / len, uy = dy / len;
      // Ray from anchor along (ux, uy). Find nearest intersection.
      let bestT = Infinity;
      let bestPoint = null;
      for (const [a, b] of targetSegments) {
        // Skip segments that belong to this line itself (shouldn't appear in
        // otherLines, but defensive against self-overlap in boundary rings).
        const hit = raySegmentIntersection(anchor, [ux, uy], a, b, EPS);
        if (hit && hit.t > EPS && hit.t < bestT) {
          bestT = hit.t;
          bestPoint = hit.point;
        }
      }
      if (!bestPoint) return { extended: false, skipped: 'noTarget' };
      return { extended: true, point: bestPoint, skipped: null };
    };

    let newStart = this.points[0];
    let newEnd = this.points[this.points.length - 1];
    let extendedStart = null; // the NEW endpoint after extension (for dashed overlay)
    let extendedEnd = null;

    if (this.intersectedStart) {
      result.startSkipped = 'alreadyIntersected';
    } else {
      const r = extendOne(true);
      if (r.extended) {
        newStart = r.point;
        extendedStart = r.point;
        this.intersectedStart = true;
        result.startExtended = true;
      } else {
        result.startSkipped = r.skipped;
        if (r.skipped === 'onTarget') this.intersectedStart = true;
      }
    }

    if (this.intersectedEnd) {
      result.endSkipped = 'alreadyIntersected';
    } else {
      const r = extendOne(false);
      if (r.extended) {
        newEnd = r.point;
        extendedEnd = r.point;
        this.intersectedEnd = true;
        result.endExtended = true;
      } else {
        result.endSkipped = r.skipped;
        if (r.skipped === 'onTarget') this.intersectedEnd = true;
      }
    }

    // Rewrite endpoints. Preserve middle points.
    if (result.startExtended || result.endExtended) {
      // Stash pre-extension original endpoints so the renderer can draw the
      // dashed overlay on just the new segment at each end.
      if (extendedStart) this.extendedStart = [this.points[0][0], this.points[0][1]];
      if (extendedEnd) this.extendedEnd = [this.points[this.points.length - 1][0], this.points[this.points.length - 1][1]];
      this.points = [newStart, ...this.points.slice(1, -1), newEnd];
    }

    return result;
  }

  // Called when an endpoint is edited. `which` is 'start' or 'end'.
  // Clears the intersected flag and the extension overlay for that end.
  resetIntersectedFlag(which) {
    if (which === 'start') {
      this.intersectedStart = false;
      this.extendedStart = null;
    } else if (which === 'end') {
      this.intersectedEnd = false;
      this.extendedEnd = null;
    } else {
      this.intersectedStart = false;
      this.intersectedEnd = false;
      this.extendedStart = null;
      this.extendedEnd = null;
    }
  }

}

export default Line;